import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Card, Button, Field, Alert, Label, Spinner, Modal } from "@/components/ui";
import { fmtDateTime } from "@/lib/helpers";
import { ShieldCheck, ShieldOff, Check, Flag, MailOpen } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const CA_URL = process.env.REACT_APP_TERMLY_CA_URL;

const GrantCheck = ({ on, onClick, label, testid }) => (
  <button onClick={onClick} data-testid={testid} className="w-full flex items-start gap-3 text-left mb-3">
    <span className={`w-6 h-6 rounded-md border-2 flex items-center justify-center shrink-0 mt-0.5 ${on ? "bg-brand border-brand" : "border-line"}`}>{on && <Check className="w-4 h-4 text-white" />}</span>
    <span className="text-sm text-ink">{label}</span>
  </button>
);

export default function Privacy() {
  const [status, setStatus] = useState(null);
  const [grantOpen, setGrantOpen] = useState(false);
  const [revoking, setRevoking] = useState(false);

  const load = useCallback(async () => setStatus(await api.consentStatus().catch(() => ({ granted: false }))), []);
  useEffect(() => { load(); }, [load]);

  const revoke = async () => {
    if (!window.confirm("Revoke ambient audio authorization? This stops KinBridge from recording future visits. Data already processed is not affected.")) return;
    setRevoking(true);
    try { await api.consentRevoke("User revoked from web privacy settings"); await load(); toast.success("Authorization revoked"); }
    catch (e) { toast.error(e?.detail || "Couldn't revoke"); }
    finally { setRevoking(false); }
  };

  if (!status) return <Spinner />;
  const granted = !!status.granted;
  const needsReauth = !!status.needs_reauth;
  const pill = granted ? { t: "Active", c: "bg-ok" } : needsReauth ? { t: "Needs re-auth", c: "bg-warn" } : { t: "Inactive", c: "bg-subtle" };

  return (
    <div className="kb-fade-up">
      <PageHeader eyebrow="KinBridge privacy" title="Privacy & consent" />
      <p className="text-sm text-subtle -mt-3 mb-5 max-w-2xl">Review and manage the authorizations KinBridge Health has on file for your account.</p>

      <Card data-testid="consent-card">
        <div className="flex items-center gap-3">
          <div className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 ${granted ? "bg-brand" : "bg-brand-tint"}`}>
            {granted ? <ShieldCheck className="w-5 h-5 text-white" /> : <ShieldOff className="w-5 h-5 text-brand-dark" />}
          </div>
          <div className="flex-1">
            <div className="font-semibold text-ink">Ambient audio authorization</div>
            <div className="text-sm text-subtle">Required to record and transcribe doctor visits.</div>
          </div>
          <span className={`text-[11px] font-bold text-white px-2.5 py-1 rounded-full ${pill.c}`} data-testid="consent-pill">{pill.t}</span>
        </div>

        <div className="border-t border-line my-4" />

        {granted ? (
          <div className="space-y-2">
            <Row label="Signed by" value={status.signature_name || "—"} />
            <Row label="Signed at" value={fmtDateTime(status.signature_at)} />
            <Row label="Disclosure version" value={status.disclosure_version || "—"} />
            <Button variant="danger" className="mt-3" data-testid="consent-revoke" loading={revoking} onClick={revoke}>Revoke authorization</Button>
          </div>
        ) : (
          <div>
            {needsReauth ? (
              <div className="flex items-start gap-2 bg-mint-tint rounded-lg p-3 mb-3" data-testid="consent-needs-reauth">
                <ShieldCheck className="w-5 h-5 text-mint-dark shrink-0" />
                <p className="text-sm text-ink">Our disclosure was updated to version <span className="font-semibold">{status.current_version}</span>. Please re-review to keep ambient recording active.</p>
              </div>
            ) : (
              <p className="text-sm text-subtle mb-3">Ambient audio recording is currently disabled. Grant authorization to enable it.</p>
            )}
            <Button data-testid="consent-grant" onClick={() => setGrantOpen(true)}>{needsReauth ? "Review & re-sign" : "Grant authorization"}</Button>
          </div>
        )}
      </Card>

      <p className="text-xs text-subtle mt-5">Questions about your data? Email privacy@kinbridgehealth.com. Revocation is prospective and does not affect data processed prior to your request.</p>

      <div className="flex flex-wrap gap-4 mt-4">
        {CA_URL && (
          <a href={CA_URL} target="_blank" rel="noopener noreferrer" data-testid="privacy-california-link" className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand hover:underline">
            <Flag className="w-4 h-4" /> Limit use of my info (CA)
          </a>
        )}
        <Link to="/legal/dsar" data-testid="privacy-dsar-link" className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand hover:underline">
          <MailOpen className="w-4 h-4" /> Submit a Data Access Request
        </Link>
      </div>

      <GrantModal open={grantOpen} onClose={() => setGrantOpen(false)} onGranted={async () => { setGrantOpen(false); await load(); toast.success("Authorization signed"); }} />
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between py-2 border-b border-line last:border-0">
      <span className="text-sm text-subtle">{label}</span>
      <span className="text-sm text-ink text-right max-w-[60%]">{value}</span>
    </div>
  );
}

function GrantModal({ open, onClose, onGranted }) {
  const [disclosure, setDisclosure] = useState(null);
  const [name, setName] = useState("");
  const [age, setAge] = useState(false);
  const [authz, setAuthz] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => { if (open) api.consentDisclosure().then(setDisclosure).catch(() => {}); }, [open]);

  const submit = async () => {
    setErr(null); setBusy(true);
    try { await api.consentGrant({ age_18_or_guardian: age, audio_recording_authorized: authz, signature_name: name.trim() }); onGranted(); }
    catch (e) { setErr(e?.detail || e?.message || "Could not save authorization"); }
    finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="HIPAA ambient audio authorization" testid="privacy-grant-modal">
      <div className="max-h-52 overflow-y-auto text-xs text-subtle whitespace-pre-line border border-line rounded-lg p-3 mb-4 bg-white">
        {disclosure?.text || "Loading disclosure…"}
      </div>
      <GrantCheck on={age} onClick={() => setAge((v) => !v)} testid="pg-age" label="I confirm I am 18 or older, or the legal guardian of the patient." />
      <GrantCheck on={authz} onClick={() => setAuthz((v) => !v)} testid="pg-authz" label="I explicitly authorize KinBridge Health to capture and process ambient audio during medical visits." />
      <Field label="Digital signature (type your full name)" data-testid="pg-signature" value={name} onChange={(e) => setName(e.target.value)} placeholder="Alex Rivera" />
      <Alert testid="pg-error">{err}</Alert>
      <Button data-testid="pg-submit" onClick={submit} loading={busy} disabled={!age || !authz || !name.trim()} className="w-full">Sign authorization</Button>
    </Modal>
  );
}
